import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';
import { CollarSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { downloadCsv } from '@/services/transportApi';

const weekAgo = () => { const d = new Date(`${todayIST()}T00:00:00`); d.setDate(d.getDate() - 6); return d.toLocaleDateString('en-CA'); };
const HEADS = ['Date', 'Section', 'Mandays', 'White / Blue / Not set', 'Manhours', 'Waiting approval', 'IT out', 'Bundles out', 'Qty out', 'Qty / manday', 'Qty / manhour'];

/** Hub tab: mandays + manhours per section per day with Internal Transfer qty moved OUT of the section. */
export const MandayReport = () => {
  const [f, setF] = useState({ date_from: weekAgo(), date_to: todayIST(), section: '', collar: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(() => {
    setLoading(true);
    staffAttApi.mandayReport(params).then(setData).catch((e) => toast.error(errMsg(e, 'Failed to load report'))).finally(() => setLoading(false));
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const csv = () => downloadCsv(`/staff-attendance/manday-report?${new URLSearchParams({ ...params, format: 'csv' })}`, `manday_report_${f.date_from}_${f.date_to}.csv`).catch((e) => toast.error(errMsg(e, 'Export failed')));
  const t = data?.totals;
  return (
    <div className="space-y-4" data-testid="manday-report">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-[160px] bg-white" data-testid="manday-from" />
        <span className="text-gray-400">→</span>
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-[160px] bg-white" data-testid="manday-to" />
        <Input value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} placeholder="Section (all)" className="w-[180px] bg-white" data-testid="manday-section" />
        <CollarSelect value={f.collar} onChange={(v) => setF({ ...f, collar: v })} testId="manday-collar-select" />
        <Button variant="outline" onClick={csv} data-testid="manday-csv-btn"><Download className="w-4 h-4 mr-1" />Download CSV</Button>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
        <span className="ml-auto text-xs text-gray-500">Mandays = approved check-ins · Manhours = approved in + out (a mid-day move is split at acceptance)</span>
      </div>
      {t && <CollarTiles split={t.collar_split} value={f.collar} onPick={(v) => setF({ ...f, collar: v })} metric="Mandays" sub={(s) => `${s.staff} staff · ${s.hours} manhours`} testId="manday-collar-tiles" />}
      {t && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[['Mandays', t.mandays, 'md'], ['Manhours', t.manhours, 'mh'], ['Waiting approval', t.pending, 'pending'], ['IT out', t.it_transfers, 'it'], ['Bundles out', t.it_bundles, 'bundles'], ['Qty out', t.it_qty, 'qty']].map(([l, v, id]) => (
            <div key={id} className="rounded-xl bg-white shadow-sm p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div><div className="text-2xl font-bold tabular-nums" data-testid={`manday-total-${id}`}>{v}</div></div>
          ))}
        </div>
      )}
      <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
        {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-amber-600" /> : (
          <Table data-testid="manday-table">
            <TableHeader><TableRow>{HEADS.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.rows.length === 0 && <TableRow><TableCell colSpan={HEADS.length} className="text-center py-8 text-gray-500" data-testid="manday-empty">No attendance or transfers in this range</TableCell></TableRow>}
              {data.rows.map((r) => (
                <TableRow key={`${r.date}-${r.section}`} data-testid={`manday-row-${r.date}-${r.section}`}>
                  <TableCell>{r.date}</TableCell><TableCell className="font-medium">{r.section}</TableCell>
                  <TableCell className="tabular-nums">{r.mandays}</TableCell>
                  <TableCell className="tabular-nums text-xs text-gray-600" data-testid={`manday-collar-${r.date}-${r.section}`}>{r.collar_mandays ? `${r.collar_mandays.white} / ${r.collar_mandays.blue} / ${r.collar_mandays.none}` : '—'}</TableCell>
                  <TableCell className="tabular-nums">{r.manhours}</TableCell>
                  <TableCell className="tabular-nums">{r.pending}</TableCell><TableCell className="tabular-nums">{r.it_transfers}</TableCell>
                  <TableCell className="tabular-nums">{r.it_bundles}</TableCell><TableCell className="tabular-nums font-semibold">{r.it_qty}</TableCell>
                  <TableCell className="tabular-nums">{r.qty_per_manday ?? '—'}</TableCell><TableCell className="tabular-nums">{r.qty_per_manhour ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
};

export default MandayReport;
